export interface BriefSection {
  number: string
  title: string
  lead?: string
  body?: string[]
  list?: string[]
}

export const briefMeta = {
  eyebrow: 'Internal · not for HCP use',
  title: 'Strategic brief',
  subtitle:
    'Rep-guided presentation · seminars, congresses, HCP visits and education · unbranded scientific exchange',
  idea: 'Your view first. Then the data.',
  ideaSupport:
    'The ophthalmologist is the expert. The rep brings a question, a dataset and a reason to discuss it — never a lesson. Every cue asks for an opinion, and no answer is treated as right or wrong.',
}

export const briefAnalogy = {
  label: 'The bumblebee analogy',
  lead: 'A win-win partnership.',
  body: [
    'Just as bumblebees fly from flower to flower collecting pollen while simultaneously pollinating the plants, representatives visit healthcare professionals to gather insights and support appropriate product understanding — while also delivering valuable medical information, education and resources.',
    'This natural exchange creates a balanced, mutually beneficial relationship — much like the quiet partnership between bees and flowers that helps the entire meadow thrive.',
  ],
  exchange: [
    {
      side: 'What the representative and company gain',
      value: 'Market understanding, real clinical perspective and appropriate product use.',
    },
    {
      side: 'What the healthcare professional gains',
      value: 'Up-to-date clinical knowledge that can support better patient care.',
    },
  ],
  implication:
    'The Snak is built as the pollination mechanic: the rep collects the HCP’s interpretation, and leaves behind a dataset and a framework worth thinking about.',
}

export const briefSections: BriefSection[] = [
  {
    number: '01',
    title: 'What this is',
    body: [
      'A rep-guided iPad presentation for use across seminars, congresses, individual HCP visits and — where appropriate — educational settings.',
      'One flow, four contexts: the core conversation runs in 60–90 seconds at a stand, and expands on request into deeper cluster comparison and study methodology for seminar or teaching use.',
    ],
  },
  {
    number: '02',
    title: 'The problem to solve',
    lead: 'The problem is double.',
    list: [
      'Representatives need a structured way to open a genuine two-way dialogue — asking for opinions rather than presenting conclusions, with no right or wrong answers on screen.',
      'The exchange must be genuinely mutual: AbbVie gathers clinical perspective while the HCP receives scientific value in return.',
    ],
    body: [
      'Solving only the first problem produces a polite quiz. Solving only the second produces a lecture. The Snak is designed to solve both at once.',
    ],
  },
  {
    number: '03',
    title: 'Audience',
    body: [
      'Nordic retina specialists and general ophthalmologists who read OCT every clinic day and are sceptical of AI claims and of being taught their own field.',
    ],
    list: [
      'Already recognise DME heterogeneity intuitively',
      'Short on time, high on scientific standards',
      'Resistant to promotional framing, open to an interesting dataset',
    ],
  },
  {
    number: '04',
    title: 'Insight',
    body: [
      'Ophthalmologists already see that DME patients look different on OCT. What they have not had is a systematic language for describing those differences.',
      'So the interesting question is not “what is DME?” but “can familiar morphology be described more systematically?”',
    ],
  },
  {
    number: '05',
    title: 'Strategic idea',
    lead: 'Your view first. Then the data.',
    body: [
      'The HCP interprets before the study is shown. Their answer becomes the entry point to the analysis, which makes the data feel like a response to their view rather than a pitch — and gives the rep something real to take back.',
    ],
  },
  {
    number: '06',
    title: 'Conversation architecture',
    list: [
      'Permission — can I get your take on one OCT question?',
      'HCP interpretation — what matters most when OCTs look different?',
      'Scientific bridge — 2,355 eyes, five familiar variables',
      'Reveal — four morphological clusters',
      'Reflection — does this resemble what you see?',
      'Credibility — what the study does and does not show',
      'Relevance — could a more systematic framework be useful?',
      'HCP-controlled depth — clusters, study design, or finish',
    ],
  },
  {
    number: '07',
    title: 'The rep’s role',
    lead: 'Facilitator and pollinator, not expert.',
    list: [
      'Use: “What do you make of this?” · “Does this resemble what you see?” · “Would you interpret that differently?” · “What would make this clinically useful?” · “Have you seen this study before?”',
      'Avoid: “Let me teach you…” · “This proves…” · “This is how you should classify…” · “AI can tell you which treatment to choose.” · “Here is what you are missing.”',
      'Never answer for the HCP, and never react to an answer as right or wrong.',
      'Leave with two things: their interpretation, and what they said would make the framework useful.',
    ],
  },
  {
    number: '08',
    title: 'Guardrails',
    lead: 'Teaser 1 territory only. Phase 3 is out of scope.',
    list: [
      'No Ozurdex, no branded product messaging',
      'No treatment recommendations or treatment-selection claims',
      'No inflammatory DME used as a treatment argument',
      'No positioning of AI as a clinical decision tool',
      'Clusters are never framed as treatment groups, response groups or validated disease stages',
      'Exploratory and morphological framing at all times; validation still required',
    ],
  },
  {
    number: '09',
    title: 'Measures of success',
    list: [
      'Share of interactions where the HCP answers at least two cues',
      'Share choosing to go deeper (cluster comparison or study design)',
      'Quality of insight collected: what the HCP says would make the framework useful',
      'Scientific value delivered in return — study design opened or requested',
      'Core conversation completed within 60–90 seconds at the stand',
    ],
  },
]
